import axiosInstance from '../config/axios';

// Tipler
export interface OfficeGrant {
  officeId: number;
  officeCode?: string;
  officeName?: string;
  isDefault: boolean;
}

export interface WarehouseGrant {
  warehouseId: number;
  warehouseCode?: string;
  warehouseName?: string;
  officeId?: number;
  isDefault: boolean;
}

export interface SalesRegionGrant {
  countryCode: string;   // ör: 'TR'
  stateCode?: string | null;    // ör: '34'
  cityCode?: string | null;
  districtCode?: string | null;
  regionName?: string;
}

export interface SalespersonGrant {
  salespersonCode: string;
  salespersonName?: string;
  isDefault: boolean;
}

export interface UserGrantsResponse {
  offices: OfficeGrant[];
  warehouses: WarehouseGrant[];
  salesRegions: SalesRegionGrant[];
  salespersons?: SalespersonGrant[];
}

// Kullanıcı Yetki Servisi
const userGrants = {
  // Kullanıcı ofisleri
  async getUserOffices(userId: string): Promise<OfficeGrant[]> {
    try {
      const { data } = await axiosInstance.get(`/api/users/${userId}/offices`);
      return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
    } catch (err: any) {
      if (err?.response?.status === 404) return [];
      throw err;
    }
  },
  async putUserOffices(userId: string, offices: OfficeGrant[]): Promise<void> {
    try {
      await axiosInstance.put(`/api/users/${userId}/offices`, offices);
    } catch (err: any) {
      if (err?.response?.status && err.response.status >= 400 && err.response.status < 500) {
        try {
          await axiosInstance.put(`/api/users/${userId}/offices`, { items: offices });
          return;
        } catch (err2: any) {
          try {
            await axiosInstance.put(`/api/users/${userId}/offices`, { data: offices });
            return;
          } catch (err3) {
            throw err3;
          }
        }
      }
      throw err;
    }
  },

  // Kullanıcı depoları
  async getUserWarehouses(userId: string): Promise<WarehouseGrant[]> {
    try {
      const { data } = await axiosInstance.get(`/api/users/${userId}/warehouses`);
      return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
    } catch (err: any) {
      if (err?.response?.status === 404) return [];
      throw err;
    }
  },
  async putUserWarehouses(userId: string, warehouses: WarehouseGrant[]): Promise<void> {
    try {
      await axiosInstance.put(`/api/users/${userId}/warehouses`, warehouses);
    } catch (err: any) {
      if (err?.response?.status && err.response.status >= 400 && err.response.status < 500) {
        try {
          await axiosInstance.put(`/api/users/${userId}/warehouses`, { items: warehouses });
          return;
        } catch (err2: any) {
          try {
            await axiosInstance.put(`/api/users/${userId}/warehouses`, { data: warehouses });
            return;
          } catch (err3) {
            throw err3;
          }
        }
      }
      throw err;
    }
  },

  // Kullanıcı satış bölgeleri (SS)
  async getUserSalesRegions(userId: string): Promise<SalesRegionGrant[]> {
    try {
      const { data } = await axiosInstance.get(`/api/users/${userId}/sales-regions`);
      return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
    } catch (err: any) {
      if (err?.response?.status === 404) return [];
      throw err;
    }
  },
  async putUserSalesRegions(userId: string, regions: SalesRegionGrant[]): Promise<void> {
    try {
      await axiosInstance.put(`/api/users/${userId}/sales-regions`, regions);
    } catch (err: any) {
      if (err?.response?.status && err.response.status >= 400 && err.response.status < 500) {
        try {
          await axiosInstance.put(`/api/users/${userId}/sales-regions`, { items: regions });
          return;
        } catch (err2: any) {
          try {
            await axiosInstance.put(`/api/users/${userId}/sales-regions`, { data: regions });
            return;
          } catch (err3) {
            throw err3;
          }
        }
      }
      throw err;
    }
  },

  // Kullanıcı satış sorumluları
  async getUserSalespersons(userId: string): Promise<SalespersonGrant[]> {
    try {
      const { data } = await axiosInstance.get(`/api/users/${userId}/salespersons`);
      return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        // Alternative paths
        try {
          const { data } = await axiosInstance.get(`/api/user-grants/${userId}/salespersons`);
          return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
        } catch (_) {
          try {
            const { data } = await axiosInstance.get(`/api/users/${userId}/salesperson-grants`);
            return (data && typeof data === 'object' && 'success' in data) ? (data.data || []) : data;
          } catch (__){
            return [];
          }
        }
      }
      throw err;
    }
  },
  async putUserSalespersons(userId: string, salespersons: SalespersonGrant[]): Promise<void> {
    let endpoint404 = false;
    try {
      await axiosInstance.put(`/api/users/${userId}/salespersons`, salespersons);
      return;
    } catch (err: any) {
      if (err?.response?.status === 404) endpoint404 = true;
      if (err?.response?.status && err.response.status >= 400 && err.response.status < 500) {
        try {
          await axiosInstance.put(`/api/users/${userId}/salespersons`, { items: salespersons });
          return;
        } catch (err2: any) {
          if (err2?.response?.status === 404) endpoint404 = true;
          try {
            await axiosInstance.put(`/api/users/${userId}/salespersons`, { data: salespersons });
            return;
          } catch (err3: any) {
            if (err3?.response?.status === 404) endpoint404 = true;
            // If path itself is missing, try alternative endpoints with same payload fallbacks
            try {
              await axiosInstance.put(`/api/user-grants/${userId}/salespersons`, salespersons);
              return;
            } catch (e1: any) {
              if (e1?.response?.status === 404) endpoint404 = true;
              try {
                await axiosInstance.put(`/api/user-grants/${userId}/salespersons`, { items: salespersons });
                return;
              } catch (e2: any) {
                if (e2?.response?.status === 404) endpoint404 = true;
                try {
                  await axiosInstance.put(`/api/user-grants/${userId}/salespersons`, { data: salespersons });
                  return;
                } catch (e3: any) {
                  if (e3?.response?.status === 404) endpoint404 = true;
                  try {
                    await axiosInstance.put(`/api/users/${userId}/salesperson-grants`, salespersons);
                    return;
                  } catch (e4: any) {
                    if (e4?.response?.status === 404) endpoint404 = true;
                    try {
                      await axiosInstance.put(`/api/users/${userId}/salesperson-grants`, { items: salespersons });
                      return;
                    } catch (e5: any) {
                      if (e5?.response?.status === 404) endpoint404 = true;
                      try {
                        await axiosInstance.put(`/api/users/${userId}/salesperson-grants`, { data: salespersons });
                        return;
                      } catch (e6: any) {
                        if (e6?.response?.status === 404) endpoint404 = true;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      if (endpoint404) {
        const unsupported: any = new Error('Satış sorumlusu yetkileri kaydı desteklenmiyor: Backend endpoint bulunamadı (404)');
        unsupported.code = 'UNSUPPORTED_404';
        unsupported.isUnsupported = true;
        unsupported.response = err?.response;
        throw unsupported;
      }
      throw err;
    }
  },

  // Aktif kullanıcının yetkileri (profil)
  async getMyGrants(): Promise<UserGrantsResponse> {
    const { data } = await axiosInstance.get('/api/me/grants');
    return (data && typeof data === 'object' && 'success' in data) ? (data.data || {}) : data;
  }
};

export default userGrants;
